// Találati oldal. A kifejezés URL-állapot (`?q=`), hogy a találat megosztható és visszalapozható
// legyen — a TanStack Router search-paramétere a forrás-igazság, nem egy komponens-state. A
// terület-szűrő szintén URL-ben él (`?terulet=`), tehát a szűkített találat is megosztható.
//
// NINCS oldalsáv-fa: a keresés nem egy területen belül zajlik, így a fának nem lenne mit mutatnia
// (korábban „a tartalom betöltése nem sikerült" hibaüzenetet írt oda, holott semmi nem hibázott).
// A tájékozódást a találatok terület-bontása adja.
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { SearchScope } from '@morgens-hub/contracts';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { KnowledgeCrumbs } from '@/components/shared/knowledge-crumbs';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { KnowledgeLayout } from '@/features/knowledge/components/knowledge-layout';
import { useTopic } from '@/features/knowledge/hooks/use-topic';
import { useSearch } from '@/features/search/api';
import { SearchScopeToggle } from '@/features/search/components/search-scope-toggle';
import { SearchSnippet } from '@/features/search/components/search-snippet';
import { requireAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

/** URL ↔ contract leképezés: a magyar `mod` érték olvasható marad a megosztott linkben. */
const MOD_TO_SCOPE: Record<'cim' | 'tartalom', SearchScope> = { cim: 'title', tartalom: 'content' };
const SCOPE_TO_MOD: Partial<Record<SearchScope, 'cim' | 'tartalom'>> = { title: 'cim', content: 'tartalom' };

const searchSchema = z.object({
  q: z.string().optional(),
  /** Terület-slug: a találatok szűkítése egy területre (kliens-oldali, a lista már megvan). */
  terulet: z.string().optional(),
  /** Hatókör-URL-állapot; hiánya = mindenben keres (a mai, alapértelmezett viselkedés). */
  mod: z.enum(['cim', 'tartalom']).optional(),
});

export const Route = createFileRoute('/kereses')({
  validateSearch: searchSchema,
  beforeLoad: ({ location }) => requireAuth(location.href),
  component: SearchPage,
});

function SearchPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { q = '', terulet, mod } = Route.useSearch();
  const scope: SearchScope = mod === undefined ? 'all' : MOD_TO_SCOPE[mod];
  const [draft, setDraft] = useState(q);
  const query = useSearch(q, scope);
  const { vars } = useTopic();

  // A címsorból (megosztott link, vissza-gomb) érkező kifejezés kerüljön a mezőbe.
  useEffect(() => {
    setDraft(q);
  }, [q]);

  const body = query.data?.status === 200 ? query.data.body : null;
  // Opcionális: régi/cache-elt válasz nem tartalmazza — a UI ilyenkor egyszerűen nem mutat sávot.
  const structureHits = body?.structureHits ?? [];
  // A contract a dokumentált 200-at típusozza, de a ts-rest runtime ismeretlen szerverválaszt is
  // visszaadhat resolved adatként — ezért a státuszt a hálózati határ valós alakjából olvassuk.
  const responseStatus = (query.data as { status: number } | undefined)?.status;
  const failed = query.isError || (responseStatus !== undefined && responseStatus !== 200);
  // Saját `useMemo`-ban: a `?? []` enélkül minden renderen ÚJ tömb-referenciát adna, ami az alábbi
  // `spaces` useMemo függőségét (`[hits]`) is minden renderen "megváltoztatná".
  const hits = useMemo(() => body?.hits ?? [], [body]);

  // A bontás a TELJES találatlistából készül, hogy a szűrő akkor is mutassa a darabszámokat, ha
  // épp egy területre van szűkítve.
  const spaces = useMemo(() => {
    const counts = new Map<string, { slug: string; name: string; count: number }>();
    for (const hit of hits) {
      const entry = counts.get(hit.spaceSlug) ?? { slug: hit.spaceSlug, name: hit.spaceName, count: 0 };
      entry.count += 1;
      counts.set(hit.spaceSlug, entry);
    }
    return [...counts.values()].sort((a, b) => b.count - a.count);
  }, [hits]);

  const visible = terulet ? hits.filter((hit) => hit.spaceSlug === terulet) : hits;
  // M1: a terület-szűrő a struktúra-találatokra is hasson — enélkül "Pénzügy"-re szűkítve is más
  // szekciók könyvei/szekciói maradtak a felső sávban. A `spaceSlug` mindkét fajtánál a SAJÁT
  // területet azonosítja: szekció-találatnál (kind='space') ez a saját slugja, könyv-találatnál a
  // szülő terület slugja — tehát ugyanaz az összehasonlítás mindkét ágra helyes.
  const visibleStructureHits = terulet
    ? structureHits.filter((hit) => hit.spaceSlug === terulet)
    : structureHits;

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    // Új kifejezésnél a terület-szűrő elesik: a korábbi terület a friss találatokban nem biztos,
    // hogy van. A hatókör (`mod`) viszont marad — az a keresés MÓDJA, nem a találat helye.
    void navigate({ to: '/kereses', search: { q: draft.trim() || undefined, mod }, replace: true });
  };

  const filterTo = (slug: string | undefined): void => {
    void navigate({
      to: '/kereses',
      search: { q: q || undefined, mod, ...(slug === undefined ? {} : { terulet: slug }) },
      replace: true,
    });
  };

  const changeScope = (nextScope: SearchScope): void => {
    void navigate({
      to: '/kereses',
      search: { q: q || undefined, terulet, mod: SCOPE_TO_MOD[nextScope] },
      replace: true,
    });
  };

  return (
    <KnowledgeLayout showTree={false}>
      <KnowledgeCrumbs current={t('search.title')} />
      <h1 className="text-read-2xl font-semibold tracking-tight">{t('search.title')}</h1>

      <form onSubmit={submit} className="relative mt-6 max-w-xl" role="search">
        <MagnifyingGlassIcon
          className="text-subtle pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2"
          aria-hidden
        />
        <Input
          autoFocus
          type="search"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          placeholder={t('search.placeholder')}
          aria-label={t('search.placeholder')}
          className="h-12 pl-12 text-base"
        />
      </form>

      <div className="mt-4">
        <SearchScopeToggle value={scope} onChange={changeScope} />
      </div>

      {visibleStructureHits.length > 0 && (
        <ul className="divide-border mt-6 max-w-3xl divide-y">
          {visibleStructureHits.map((hit) =>
            hit.kind === 'space' ? (
              <li key={hit.id} className="flex items-center gap-2 py-3">
                <StructureBadge kindKey="space" />
                <Link
                  to="/hub/$spaceSlug"
                  params={{ spaceSlug: hit.spaceSlug }}
                  className="hover:text-topic font-medium"
                >
                  {hit.name}
                </Link>
              </li>
            ) : (
              <li key={hit.id} className="flex items-center gap-2 py-3">
                <StructureBadge kindKey="category" />
                <Link
                  to="/hub/$spaceSlug"
                  params={{ spaceSlug: hit.spaceSlug }}
                  // A `hash` prop `exactOptionalPropertyTypes` alatt nem fogad explicit `undefined`-t —
                  // a category-találat `categorySlug`-ja a kontraktus szerint mindig kitöltött, a `null`
                  // csak a `space` ágnál fordul elő (ott nincs is `hash`).
                  {...(hit.categorySlug === null ? {} : { hash: hit.categorySlug })}
                  className="hover:text-topic font-medium"
                >
                  {hit.name}
                </Link>
              </li>
            ),
          )}
        </ul>
      )}

      {query.isLoading && (
        <div className="mt-8 space-y-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {!query.isLoading && q.trim() && failed && (
        <p className="text-destructive mt-8 text-sm">{t('search.error')}</p>
      )}

      {body?.degraded && <p className="text-destructive mt-8 text-sm">{t('search.degraded')}</p>}

      {/* I4: a struktúra-találatot is figyelembe kell vennie — enélkül a belső oldal EGYSZERRE
        mutatott struktúra-találatot ÉS "Nincs találat"-ot (a publikus /sugo/index.tsx ezt már
        helyesen kezelte, ez a fix egységesíti a két felület viselkedését). */}
      {body && !body.degraded && hits.length === 0 && structureHits.length === 0 && q.trim() && (
        <p className="text-muted-foreground mt-8 text-sm">{t('search.empty', { query: q })}</p>
      )}

      {hits.length > 0 && (
        <>
          {/* Terület-szűrő: a találatok szóródása önmagában is információ („ez négy termékben van"). */}
          {spaces.length > 1 && (
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <FilterChip
                active={terulet === undefined}
                onClick={() => {
                  filterTo(undefined);
                }}
              >
                {t('search.allSpaces', { count: hits.length })}
              </FilterChip>
              {spaces.map((space) => (
                <FilterChip
                  key={space.slug}
                  active={terulet === space.slug}
                  topicVars={vars(space.slug)}
                  onClick={() => {
                    filterTo(space.slug);
                  }}
                >
                  <span className="bg-topic h-2 w-2 shrink-0 rounded-[2px]" aria-hidden />
                  {space.name}
                  <span className="font-mono tabular-nums">{space.count}</span>
                </FilterChip>
              ))}
            </div>
          )}

          <ul className="divide-border mt-4 max-w-3xl divide-y">
            {visible.map((hit) => (
              <li key={hit.chunkId} data-topic style={vars(hit.spaceSlug)}>
                <Link
                  to="/hub/$spaceSlug/$categorySlug/$articleSlug"
                  params={{
                    spaceSlug: hit.spaceSlug,
                    categorySlug: hit.categorySlug,
                    articleSlug: hit.articleSlug,
                  }}
                  className="index-row hover:bg-muted/50 group block py-4"
                >
                  {/* Hol van: a terület a saját színével, utána az út a cikkig. */}
                  <span className="flex flex-wrap items-center gap-1.5 text-xs">
                    <span className="text-topic font-semibold uppercase tracking-[0.1em]">
                      {hit.spaceName}
                    </span>
                    <span className="text-subtle">
                      <span aria-hidden>›</span> {hit.categoryName}
                    </span>
                  </span>

                  <span className="group-hover:text-topic text-read-lg mt-1 block font-medium transition-colors duration-150">
                    {hit.articleTitle}
                  </span>

                  <SearchSnippet snippet={hit.snippet} />
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {!q.trim() && !query.isLoading && (
        <p className="text-muted-foreground mt-8 text-sm">{t('search.hint')}</p>
      )}
    </KnowledgeLayout>
  );
}

/** A struktúra-találat sor bal oldali badge-e: „Könyv" vagy „Szekció". */
function StructureBadge({ kindKey }: { kindKey: 'category' | 'space' }): JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="border-border text-muted-foreground shrink-0 rounded-full border px-2 py-0.5 text-xs uppercase tracking-[0.08em]">
      {t(`search.kind.${kindKey}`)}
    </span>
  );
}

function FilterChip({
  active,
  topicVars,
  onClick,
  children,
}: {
  active: boolean;
  topicVars?: React.CSSProperties;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      data-topic
      style={topicVars}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors duration-150',
        active
          ? 'border-topic/60 bg-topic/10 text-foreground font-medium'
          : 'border-border text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
